import React from 'react';
import moment from 'moment';
import {Icon} from 'semantic-ui-react';
import {FormattedMessage} from 'react-intl';
import {hasMonetaryValue, isSponsored, PrizeItemTypes, requiresW9AndAffidavit} from './utils';


export default {
  sections: [
    {
      key: 'editPrizeSection',
      props: {
        headerKey: 'editprize.edit.heading'
      },

      fields: [
        {
          name: 'title',
          type: 'text',
          required: true,

          props: {
            labelKey: 'editprize.edit.title.label',
            placeholderKey: 'editprize.edit.title.placeholder'
          }
        },    
        {
          name: 'description',
          type: 'textarea',
          required: true,

          props: {
            labelKey: 'editprize.edit.description.label'
          }
        },
        {
          name: 'imageId',
          idFor: 'image',
          type: 'image',
          required: true,

          props: {
            labelKey: 'editprize.edit.imageId.label',
            imageType: 'sweepsPrize'
          }
        },
        {
          name: 'orderLink',
          type: 'textarea',
          props: {
            labelKey: 'editprize.edit.orderLink.label'
          }
        },
        {
          name: 'numToAward',
          type: 'number',
          required: true,

          props: {
            labelKey: 'editprize.edit.numToAward.label',
            min: 1,
            max: 600,
            validationRules: [
              {
                type: "InclusiveRange",
                params: {
                  min: 1,
                  max: 600
                }
              }
            ]
          }
        },
        {
          name: 'costToEnter',
          type: 'number',
          showField: formProxy => !isSponsored(formProxy.currentResource),

          props: {
            labelKey: 'editprize.edit.costToEnter.label',
            min: 1,
            max: (10 * 1000),
            validationRules: [
              {
                type: "InclusiveRange",
                params: {
                  min: 1,
                  max: (10 * 1000)
                }
              },
              {
                type: "NotNullOrBlank"
              }
            ]
          }
        },
        {
          name: 'itemType',
          type: 'radio',
          required: true,

          props: {
            labelKey: 'editprize.edit.itemType.label',
            colCount: 4,
            options: [
              {
                value: PrizeItemTypes.POINTS,
                textKey: 'editprize.edit.itemType.options.points'
              },
              {
                value: PrizeItemTypes.GIFT_CARD,
                textKey: 'editprize.edit.itemType.options.physicalCard'
              },
              {
                value: PrizeItemTypes.VIRTUAL_GIFT_CARD,
                textKey: 'editprize.edit.itemType.options.virtualCard'
              },
              {
                value: PrizeItemTypes.OTHER,
                textKey: 'editprize.edit.itemType.options.other'
              }
            ]
          }
        },
        {
          subSection: true,
          viewSwitch: formProxy => formProxy.currentResource.itemType,
          viewCases: formProxy => {
            const numericInput = [{
              name: 'value',
              type: 'number',
              required: true,
              props: {
                labelKey: 'editprize.edit.value.label'
              }
            }];

            const validatedNumericInput = [{
              name: 'value',
              type: 'number',
              required: true,
              props: {
                labelKey: 'editprize.edit.value.label',
                min: 1,
                max: 100 * 1000,
                validationRules: [
                  {
                    type: 'InclusiveRange',
                    params: {
                      min: 1,
                      max: 100 * 1000
                    }
                  },
                  {
                    type: 'NotNullOrBlank'
                  }
                ]
              }
            }];

            const currencyInput = [{
              name: 'value',
              type: 'currency',
              required: true,
              showExtraContent: formProxy => {
                const prize = formProxy.currentResource;
                return hasMonetaryValue(prize) && requiresW9AndAffidavit(prize);
              },
              props: {
                labelKey: 'editprize.edit.value.label',
                extraContent: (
                  <div className='sponsored-prize-value-extra-content'>
                    <Icon name='flag' />
                    <FormattedMessage id='editprize.edit.value.extraContent' />
                  </div>
                )
              }
            }];

            return {
              [PrizeItemTypes.COINS]: numericInput,
              [PrizeItemTypes.POINTS]: validatedNumericInput,
              [PrizeItemTypes.OTHER]: currencyInput,
              [PrizeItemTypes.GIFT_CARD]: currencyInput,
              [PrizeItemTypes.VIRTUAL_GIFT_CARD]: currencyInput
            };
          }
        },
        {
          name: 'passThroughCost',
          type: 'yesnoradio',
          showField: formProxy => isSponsored(formProxy.currentResource),
          required: true,

          props: {
            labelKey: 'editprize.edit.passThroughCost.label'
          }
        },
        {
          name: 'passThroughAmount',
          type: 'currency',
          showField: formProxy => formProxy.currentResource.passThroughCost,
          required: formProxy => formProxy.currentResource.passThroughCost,

          props: {
            labelKey: 'editprize.edit.passThroughAmount.label'
          }
        },
        {
          name: 'redrawWinners',
          type: 'yesnoradio',
          required: true,

          props: {
            labelKey: 'editprize.edit.redrawWinners.label'
          }
        },
        {
          name: 'startTime',
          type: 'datetime',
          required: true,

          props: {
            labelKey: 'editprize.edit.startTime.label',
            placeholderKey: 'editprize.edit.startTime.placeholder',
            timeSnap: {
              hour: 0,
              minute: 1,
              second: 0,
              millisecond: 0
            }
          }
        },
        {
          name: 'endTime',
          type: 'datetime',
          required: true,
          autoPopulate: formProxy => {
            const {endTime, startTime} = formProxy.currentResource;
            if (!isSponsored(formProxy.currentResource)) {

              // if this field has no value but `startTime` does, auto-populate it with a
              // date 30 days after `startTime`.
              if (!endTime && startTime) {
                return moment(startTime).add(30, 'days');
              }
            }
            return endTime;
          },

          props: {
            labelKey: 'editprize.edit.endTime.label',
            placeholderKey: 'editprize.edit.endTime.placeholder',
            timeSnap: {
              hour: 23,
              minute: 59,
              second: 59,
              millisecond: 999
            }
          }
        },
        {
          name: 'weeklyWinners',
          type: 'yesnoradio',
          required: true,

          props: {
            labelKey: 'editprize.edit.weeklyWinners.label'
          }
        },
        {
          name: 'drawInfo',
          type: 'drawInfo',
          required: true,
          showField: formProxy => formProxy.currentResource.weeklyWinners,

          props: {
            labelKey: 'editprize.edit.prizeDrawInfo.label'
          }
        },
        {
          name: 'winnerSelectionDate',
          type: 'datetime',
          disabled: (formProxy) => formProxy.currentBaseResource.type !== "CHALLENGE",
          required: true,
          showField: formProxy => !formProxy.currentResource.weeklyWinners,
          autoPopulate: formProxy => {
            const {winnerSelectionDate, endTime} = formProxy.currentResource;
            if (!isSponsored(formProxy.currentResource)) {

              // auto-populate if new value would be different then current
              // date 3 hours after `endTime`.
              if (endTime) {
                let n = moment(endTime).add(3, 'hours');
                let stamp = n.unix() * 1000;
                if (stamp !== winnerSelectionDate) {
                  return n;
                }
              }
            }
          },

          props: {
            labelKey: 'editprize.edit.winnerSelectionDate.label',
            placeholderKey: 'editprize.edit.winnerSelectionDate.placeholder',
          }
        },
        {
          name: 'restrictions',
          type: 'textarea',

          props: {
            labelKey: 'editprize.edit.restrictions.label'
          }
        },
        {
          name: 'notes',
          type: 'textarea',

          props: {
            labelKey: 'editprize.edit.notes.label'
          }
        },
          {
              name: 'showWinnersOnWinnersPage',
              type: 'yesnoradio',
              showField: formProxy => isSponsored(formProxy.currentResource),

              props: {
                  labelKey: 'editprize.edit.showWinnersOnWinnersPage.label'
              }
          },
          {
              name: 'autoSendEmail',
              type: 'yesnoradio',
              showField: formProxy => isSponsored(formProxy.currentResource),

              props: {
                  labelKey: 'editprize.edit.autoSendEmail.label'
              }
          }
      ]
    }
  ]
};



// WEBPACK FOOTER //
// ./public/pages/EditPrize/editPrizeConfig.js